import { useEffect, useState } from "react";
import { Loader2, Phone } from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { mapIntegrationToCampaignOption } from "@/lib/campaign-integration-options";
import type { CampaignIntegrationOption } from "@/lib/automation-campaign";

function IntegrationOptionLabel({ item }: { item: CampaignIntegrationOption }) {
  return (
    <span className="flex min-w-0 items-center gap-2 overflow-hidden">
      <Phone className="h-3.5 w-3.5 shrink-0 text-green-500" />
      <span className="min-w-0 truncate">
        {item.display_name}
        {item.detail ? (
          <span className="text-xs text-muted-foreground"> ({item.detail})</span>
        ) : null}
      </span>
    </span>
  );
}

interface DisparoIntegrationSelectProps {
  workspaceId: string;
  value: string;
  onChange: (integrationId: string) => void;
}

export function DisparoIntegrationSelect({
  workspaceId,
  value,
  onChange,
}: DisparoIntegrationSelectProps) {
  const [integrations, setIntegrations] = useState<CampaignIntegrationOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("integrations")
        .select("id, provider, config, instance_identifier, status")
        .eq("workspace_id", workspaceId)
        .eq("provider", "whatsapp_uazapi")
        .eq("status", "active")
        .order("created_at", { ascending: true });

      if (cancelled) return;
      if (!error && data) {
        setIntegrations(
          data.map((row) =>
            mapIntegrationToCampaignOption(
              row as Parameters<typeof mapIntegrationToCampaignOption>[0],
            ),
          ),
        );
        if (!value && data.length === 1) {
          onChange(data[0].id);
        }
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, value, onChange]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        Carregando conexões WhatsApp...
      </div>
    );
  }

  if (integrations.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Nenhuma conexão WhatsApp ativa. Configure em Integrações.
      </p>
    );
  }

  const selected = integrations.find((item) => item.id === value);

  return (
    <div className="space-y-1.5">
      <Label>Conexão WhatsApp</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="min-w-0 gap-2 [&>span]:min-w-0 [&>span]:flex-1 [&>span]:overflow-hidden [&>span]:text-ellipsis [&>svg]:shrink-0">
          <SelectValue placeholder="Selecione a instância...">
            {selected ? <IntegrationOptionLabel item={selected} /> : null}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {integrations.map((item) => (
            <SelectItem
              key={item.id}
              value={item.id}
              className="min-w-0 overflow-hidden [&>span:last-child]:min-w-0 [&>span:last-child]:truncate"
            >
              <IntegrationOptionLabel item={item} />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
